import DescriptionIcon from "@mui/icons-material/Description";
import {
  Box,
  Button,
  Grid2,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getReferenceTableById } from "../../../api/reference";
import { useErrorHandler, useFormStyles, useReferenceData } from "../../../hooks";
import { CreateFlowTestMeasurementRequest } from "../../../types/model/flowTestMeasurement";
import { VariableResponse } from "../../../types/model/variable";
import FormAutocomplete from "../../General/FormAutocomplete";
import { MeasurementInGroup } from "./FlowTestMeasurements";
interface EditCellProps {
  measurement: MeasurementInGroup;
  isEditing: boolean;
  value: string;
  flag: string;
  notes: string;
  setValue: (v: string) => void;
  setFlag: (v: string) => void;
  setNotes: (v: string) => void;
}

export const EditCell = ({
  measurement,
  isEditing,
  value,
  flag,
  notes,
  setValue,
  setFlag,
  setNotes,
}: EditCellProps) => {
  const { t } = useTranslation();

  if (!isEditing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
        <Typography variant="body2">{measurement.flag} {measurement.value} {measurement.variableUnit}</Typography>

        {measurement.notes && (
          <Tooltip title={measurement.notes}>
            <DescriptionIcon
              fontSize="small"
              color="secondary"
              sx={{ cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Grid2 container spacing={1.5} alignItems="flex-start" sx={{ mt: 0.5 }}>
      <Grid2 size={{ xs: 12, md: 2.5 }}>
        <TextField
          fullWidth
          label={t("flow-tests.flow-test-measurement.flag")}
          value={flag}
          onChange={(e) => setFlag(e.target.value)}
          variant="outlined"
        />
      </Grid2>

      <Grid2 size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          type="number"
          label={t("flow-tests.flow-test-measurement.value")}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          variant="outlined"
          autoFocus
        />
      </Grid2>

      <Grid2 size={{ xs: 12, md: 5.5 }}>
        <TextField
          fullWidth
          label={t("flow-tests.flow-test-measurement.notes")}
          multiline
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          variant="outlined"
        />
      </Grid2>
    </Grid2>
  );
};

interface AddMeasurementRowProps {
  flowTestId: number;
  entityId: number;
  measuredCaptureId: number;
  measuredAt?: string | null;
  testingTime?: number | null;
  onCreate: (values: CreateFlowTestMeasurementRequest) => Promise<void>;
  onCancel: () => void;
  variables: VariableResponse[];
  isEditing: boolean;
  isSaving: boolean;
}

export const AddMeasurementRow = ({
  flowTestId,
  measuredCaptureId,
  measuredAt,
  testingTime,
  onCreate,
  onCancel,
  variables,
  isEditing,
  isSaving,
}: AddMeasurementRowProps) => {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(isEditing);
  const methods = useForm<{
    variableId: number | null;
    value: string;
    flag: string;
    notes: string;
  }>({
    defaultValues: { variableId: null, value: "", flag: "", notes: "" },
  });

  const { handleSubmit, watch } = methods;

  const onSubmit = handleSubmit(async (data) => {
    if (!data.variableId || !data.value) return;
    await onCreate({
      flowTestId,
      measuredCaptureId,
      variableId: data.variableId,
      value: Number(data.value),
      measuredAt: measuredAt ?? undefined,
      testingTime: testingTime ?? undefined,
      flag: data.flag || undefined,
      notes: data.notes || undefined,
    } as CreateFlowTestMeasurementRequest);
    onCancel();
  });

  const variableId = methods.watch("variableId");
  const selectedVariable = variables.find((v: any) => v.id === variableId);
  const [lookupTableInfo, setLookupTableInfo] = useState<{ schemaName: string, tableName: string } | null>(null);

  useEffect(() => {
    if (selectedVariable?.isLookup && selectedVariable.lookupTableId) {
      getReferenceTableById(selectedVariable.lookupTableId)
        .then((data) => setLookupTableInfo({schemaName: data.schemaName, tableName: data.tableName}) )
        .catch(error => {
          handleError(error);
          setLookupTableInfo(null);
        });
    } else {
      setLookupTableInfo(null);
    }
  }, [selectedVariable, methods]);
  
  const { data: lookupOptions = [] } = useReferenceData(
    lookupTableInfo?.schemaName as string,
    lookupTableInfo?.tableName as string
  );
  

  return (
    <FormProvider {...methods}>
      <Grid2 container spacing={1} sx={{ py: 1 }}>
        <Grid2 size={{ xs: 12, md: 4.5 }}>
          <FormAutocomplete
            name="variableId"
            label={`${t("flow-tests.flow-test-measurement.variable")} *`}
            options={variables}
            isEditing={isEditing}
            isSaving={isSaving}
            disabled={false}
            formStyles={formStyles}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 1.5 }}>
          <TextField
            fullWidth
            label={t("flow-tests.flow-test-measurement.flag")}
            variant="outlined"
            disabled={isSaving}
            sx={{ ...formStyles }}
            {...methods.register("flag")}
            error={!!methods.formState.errors.flag}
            helperText={methods.formState.errors.flag?.message}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 3.5 }}>
          {selectedVariable?.isLookup ? (
            <FormAutocomplete
              name="value"
              label={`${t("flow-tests.flow-test-measurement.value")} *`}
              options={lookupOptions}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={!isEditing || isSaving || !variableId}
              formStyles={formStyles}
            />
          ) : (
            <TextField
              fullWidth
              label={`${t("flow-tests.flow-test-measurement.value")} *`}
              variant="outlined"
              disabled={isSaving || !variableId}
              type="number"
              {...methods.register("value", { valueAsNumber: true })}
              error={!!methods.formState.errors.value}
              helperText={methods.formState.errors.value?.message}
            />
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, md: 2.5 }} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", gap: 1, mt: 1.5 }}>
          <Button
            size="small"
            variant="contained"
            onClick={onSubmit}
            disabled={!watch("variableId") || !watch("value")}
          >
            {t("generic.add")}
          </Button>
          <Button size="small" onClick={onCancel}>
            {t("generic.cancel")}
          </Button>
        </Grid2>

        <Grid2 size={9.5}>
          <TextField
            fullWidth
            variant={isEditing ? "outlined" : "standard"}
            disabled={!isEditing || isSaving}
            label={t("flow-tests.flow-test-measurement.notes")}
            placeholder={t("flow-tests.flow-test-measurement.notes-placeholder")}
            multiline
            minRows={4}
            sx={{ ...formStyles }}
            {...methods.register("notes")}
            error={!!methods.formState.errors.notes}
            helperText={methods.formState.errors.notes?.message}
          />
        </Grid2>

      </Grid2>
    </FormProvider>
  );
};